import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import Layout from '../components/common/Layout';
import WhoseStorySelector from '../components/stories/WhoseStorySelector';
import StoryTypeGrid from '../components/stories/StoryTypeGrid';
import MediaUploadGrid from '../components/stories/MediaUploadGrid';
import ContextBanner from '../components/stories/ContextBanner';
import { axiosInstance } from '../services/api';
import { createWithMedia, createLocalMediaFile, detectMediaType } from '../services/mediaService';

// ── Accent colour palette ────────────────────────────────────────────────
const ACCENT = {
  olive: {
    color: 'text-primary-green',
    border: 'border-primary-green',
    bg: 'bg-green-50',
    bgSolid: 'bg-primary-green',
    ring: 'ring-primary-green',
  },
  orange: {
    color: 'text-highlight-orange',
    border: 'border-highlight-orange',
    bg: 'bg-orange-50',
    bgSolid: 'bg-highlight-orange',
    ring: 'ring-highlight-orange',
  },
  blue: {
    color: 'text-blue-700',
    border: 'border-blue-600',
    bg: 'bg-blue-50',
    bgSolid: 'bg-blue-600',
    ring: 'ring-blue-600',
  },
  teal: {
    color: 'text-teal-700',
    border: 'border-teal-600',
    bg: 'bg-teal-50',
    bgSolid: 'bg-teal-600',
    ring: 'ring-teal-600',
  },
};

function deriveAccent(tier, selectedChildIds, children) {
  if (tier !== 'child' || selectedChildIds.length === 0) return ACCENT.olive;
  if (selectedChildIds.length >= 2) return ACCENT.teal;
  // single child — pick by child's accentColor field, fallback orange/blue
  const child = children.find(c => c.id === selectedChildIds[0]);
  if (child?.accentColor === 'blue') return ACCENT.blue;
  if (child?.accentColor === 'orange') return ACCENT.orange;
  // Default for single child: use orange for odd index, blue for even
  const idx = children.findIndex(c => c.id === selectedChildIds[0]);
  return idx % 2 === 0 ? ACCENT.orange : ACCENT.blue;
}

// ── Template ID for stories (same as TellAStory.js) ─────────────────────
const STORY_TEMPLATE_ID = 12;

const CreateStory = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  // ── Form state ───────────────────────────────────────────────────────
  const [tier, setTier] = useState('mine');
  const [children, setChildren] = useState([]);
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [storyType, setStoryType] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [location, setLocation] = useState('');
  const [localMediaFiles, setLocalMediaFiles] = useState([]);

  // ── UI state ─────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // ── Refs for scroll-to-error ─────────────────────────────────────────
  const sectionRefs = {
    tier: useRef(null),
    storyType: useRef(null),
    title: useRef(null),
    content: useRef(null),
  };

  // ── Accent colour derivation ─────────────────────────────────────────
  const accentClasses = deriveAccent(tier, selectedChildIds, children);

  // ── Fetch children on mount ──────────────────────────────────────────
  useEffect(() => {
    const fetchChildren = async () => {
      setLoadingChildren(true);
      try {
        const response = await axiosInstance.get('/genealogy/people/my_family_grouped/');
        const data = response.data;
        // children array from the grouped endpoint
        const kids = data.children || [];
        setChildren(kids);
      } catch (err) {
        console.error('Failed to fetch children:', err);
      } finally {
        setLoadingChildren(false);
      }
    };
    fetchChildren();
  }, []);

  // ── Edit mode: fetch existing story ──────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    const fetchStory = async () => {
      setLoadingEdit(true);
      try {
        const response = await axiosInstance.get(`/lifeevent/events/${editId}/`);
        const event = response.data;
        setTitle(event.title || '');
        setContent(event.story || event.story_responses?.what_happened || '');
        setStoryType(event.event_data?.story_type || '');
        setSelectedDate(event.date || '');
        setLocation(event.location || '');
        // Restore tier + children if present
        if (event.event_data?.tier === 'child' && event.event_data?.child_ids) {
          setTier('child');
          setSelectedChildIds(event.event_data.child_ids);
        }
      } catch (err) {
        console.error('Failed to load story for editing:', err);
        setError('Could not load this story. Please try again.');
      } finally {
        setLoadingEdit(false);
      }
    };
    fetchStory();
  }, [isEdit, editId]);

  // ── Cleanup preview URLs on unmount ──────────────────────────────────
  useEffect(() => {
    return () => {
      localMediaFiles.forEach(media => {
        if (media.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(media.preview);
        }
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Child selection handlers ─────────────────────────────────────────
  const handleToggleChild = (childId) => {
    setSelectedChildIds(prev =>
      prev.includes(childId)
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const handleToggleAll = () => {
    if (selectedChildIds.length === children.length) {
      setSelectedChildIds([]);
    } else {
      setSelectedChildIds(children.map(c => c.id));
    }
  };

  // ── Media handlers ───────────────────────────────────────────────────
  const handleFilesSelected = (files) => {
    const newMedia = files.map(file => {
      const mediaType = detectMediaType(file);
      return createLocalMediaFile({
        file,
        preview: mediaType === 'image' ? URL.createObjectURL(file) : null,
        type: mediaType,
        name: file.name,
      });
    });
    setLocalMediaFiles(prev => [...prev, ...newMedia]);
  };

  const handleRemoveFile = (mediaId) => {
    setLocalMediaFiles(prev => {
      const removed = prev.find(m => m.id === mediaId);
      if (removed?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.preview);
      }
      return prev.filter(m => m.id !== mediaId);
    });
  };

  // ── Validation ───────────────────────────────────────────────────────
  const validate = () => {
    const errors = {};

    if (tier === 'child' && selectedChildIds.length === 0) {
      errors.tier = 'Please select at least one child.';
    }
    if (!storyType) {
      errors.storyType = 'Please choose a story type.';
    }
    if (!title.trim()) {
      errors.title = 'Give your story a title.';
    }
    if (!content.trim()) {
      errors.content = 'Tell us what happened.';
    }

    setFieldErrors(errors);

    // Scroll to first error
    const firstKey = Object.keys(errors)[0];
    if (firstKey && sectionRefs[firstKey]?.current) {
      sectionRefs[firstKey].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return Object.keys(errors).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const eventData = {
        template: STORY_TEMPLATE_ID,
        title: title.trim(),
        date: selectedDate || new Date().toISOString().split('T')[0],
        location: location.trim() || null,
        event_data: {
          story_type: storyType,
          tier,
          child_ids: tier === 'child' ? selectedChildIds : [],
        },
        story: content.trim(),
        story_responses: {
          what_happened: content.trim(),
          who_was_involved: tier === 'mine' ? 'Me' : 'My child',
          why_important: storyType,
        },
        is_private: false,
        sharing_scope: 'single_family',
        event_people: tier === 'child' ? selectedChildIds : [],
      };

      const mediaFiles = localMediaFiles.map(m => ({
        file: m.file,
        name: m.name,
      }));

      if (isEdit) {
        await axiosInstance.put(`/lifeevent/events/${editId}/`, eventData);
      } else {
        await createWithMedia('/lifeevent/events/', eventData, mediaFiles);
      }

      navigate('/stories');
    } catch (err) {
      console.error('Failed to save story:', err);
      setError(err.response?.data?.message || err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Loading state for edit mode ──────────────────────────────────────
  if (loadingEdit) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 size={32} className="animate-spin text-primary-green" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-32 sm:pb-12">
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-6 sm:mb-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-200 transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center"
          >
            <ArrowLeft size={24} className="text-text-dark" />
          </button>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-text-dark">
            {isEdit ? 'Edit Story' : 'Share a Story'}
          </h1>
        </div>

        {/* ── Global error ────────────────────────────────────────── */}
        {error && (
          <div className="flex items-start gap-3 px-4 py-3 mb-6 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-base text-red-700">{error}</p>
          </div>
        )}

        {/* ── Section 1: Whose Story ──────────────────────────────── */}
        <section ref={sectionRefs.tier} className="mb-8">
          <label className="block text-xl font-serif font-semibold text-text-dark mb-3">
            Whose story is this?
          </label>
          <WhoseStorySelector
            tier={tier}
            onTierChange={(t) => {
              setTier(t);
              if (t === 'mine') setSelectedChildIds([]);
              setFieldErrors(prev => ({ ...prev, tier: undefined }));
            }}
            children={children}
            selectedChildIds={selectedChildIds}
            onToggleChild={(id) => {
              handleToggleChild(id);
              setFieldErrors(prev => ({ ...prev, tier: undefined }));
            }}
            onToggleAll={handleToggleAll}
            accentClasses={accentClasses}
          />
          {fieldErrors.tier && (
            <p className="mt-2 text-base text-red-600">{fieldErrors.tier}</p>
          )}
          <div className="mt-3">
            <ContextBanner
              tier={tier}
              selectedChildIds={selectedChildIds}
              children={children}
              accentClasses={accentClasses}
            />
          </div>
        </section>

        {/* ── Section 2: What Happened ────────────────────────────── */}
        <section className="mb-8 space-y-6">
          {/* Story type */}
          <div ref={sectionRefs.storyType}>
            <label className="block text-xl font-serif font-semibold text-text-dark mb-3">
              What kind of story?
            </label>
            <StoryTypeGrid
              selected={storyType}
              onSelect={(v) => {
                setStoryType(v);
                setFieldErrors(prev => ({ ...prev, storyType: undefined }));
              }}
              accentClasses={accentClasses}
            />
            {fieldErrors.storyType && (
              <p className="mt-2 text-base text-red-600">{fieldErrors.storyType}</p>
            )}
          </div>

          {/* Title */}
          <div ref={sectionRefs.title}>
            <label htmlFor="story-title" className="block text-xl font-serif font-semibold text-text-dark mb-2">
              Title
            </label>
            <input
              id="story-title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setFieldErrors(prev => ({ ...prev, title: undefined }));
              }}
              placeholder="Give your story a title..."
              className={`w-full px-4 py-3 text-lg rounded-lg border-2 bg-white text-text-dark placeholder-gray-400 focus:outline-none focus:ring-2 min-h-[48px] transition-colors ${
                fieldErrors.title
                  ? 'border-red-400 focus:ring-red-300'
                  : `border-border-gray focus:${accentClasses.ring} focus:border-transparent`
              }`}
            />
            {fieldErrors.title && (
              <p className="mt-1 text-base text-red-600">{fieldErrors.title}</p>
            )}
          </div>

          {/* Content */}
          <div ref={sectionRefs.content}>
            <label htmlFor="story-content" className="block text-xl font-serif font-semibold text-text-dark mb-2">
              What happened?
            </label>
            <textarea
              id="story-content"
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setFieldErrors(prev => ({ ...prev, content: undefined }));
              }}
              placeholder="Tell the story in your own words..."
              rows={6}
              className={`w-full px-4 py-3 text-lg rounded-lg border-2 bg-white text-text-dark placeholder-gray-400 focus:outline-none focus:ring-2 resize-y min-h-[144px] transition-colors ${
                fieldErrors.content
                  ? 'border-red-400 focus:ring-red-300'
                  : `border-border-gray focus:${accentClasses.ring} focus:border-transparent`
              }`}
            />
            {fieldErrors.content && (
              <p className="mt-1 text-base text-red-600">{fieldErrors.content}</p>
            )}
          </div>

          {/* Date */}
          <div>
            <label htmlFor="story-date" className="block text-xl font-serif font-semibold text-text-dark mb-2">
              When did this happen?
              <span className="text-base font-normal text-text-medium ml-2">(optional)</span>
            </label>
            <input
              id="story-date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-3 text-lg rounded-lg border-2 border-border-gray bg-white text-text-dark focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent min-h-[48px]"
            />
          </div>

          {/* Location */}
          <div>
            <label htmlFor="story-location" className="block text-xl font-serif font-semibold text-text-dark mb-2">
              Where did it happen?
              <span className="text-base font-normal text-text-medium ml-2">(optional)</span>
            </label>
            <input
              id="story-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="City, state, or place name..."
              className="w-full px-4 py-3 text-lg rounded-lg border-2 border-border-gray bg-white text-text-dark placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent min-h-[48px]"
            />
          </div>
        </section>

        {/* ── Section 3: Media ────────────────────────────────────── */}
        {!isEdit && (
          <section className="mb-8">
            <label className="block text-xl font-serif font-semibold text-text-dark mb-3">
              Add photos or recordings
              <span className="text-base font-normal text-text-medium ml-2">(optional)</span>
            </label>
            <MediaUploadGrid
              mediaFiles={localMediaFiles}
              onFilesSelected={handleFilesSelected}
              onRemoveFile={handleRemoveFile}
            />
          </section>
        )}

        {/* ── Section 4: Desktop Submit Bar ───────────────────────── */}
        <div className="hidden sm:flex justify-end gap-3 pt-4 border-t border-border-gray">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 text-lg font-medium rounded-lg border-2 border-border-gray text-text-dark bg-white hover:bg-gray-50 transition-colors min-h-[48px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-8 py-3 text-lg font-semibold rounded-lg text-white transition-colors min-h-[48px] flex items-center gap-2 ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : `${accentClasses.bgSolid} hover:opacity-90`
            }`}
          >
            {isSubmitting && <Loader2 size={20} className="animate-spin" />}
            {isEdit ? 'Save Changes' : 'Share Story'}
          </button>
        </div>
      </div>

      {/* ── Mobile Sticky Submit Bar ──────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 sm:hidden bg-white border-t border-border-gray px-4 py-3 flex gap-3 z-50">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex-1 px-4 py-3 text-lg font-medium rounded-lg border-2 border-border-gray text-text-dark bg-white min-h-[48px]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`flex-1 px-4 py-3 text-lg font-semibold rounded-lg text-white min-h-[48px] flex items-center justify-center gap-2 ${
            isSubmitting
              ? 'bg-gray-400 cursor-not-allowed'
              : `${accentClasses.bgSolid} hover:opacity-90`
          }`}
        >
          {isSubmitting && <Loader2 size={20} className="animate-spin" />}
          {isEdit ? 'Save' : 'Share'}
        </button>
      </div>
    </Layout>
  );
};

export default CreateStory;
